import React, { useEffect, useState } from 'react';
import { Loader2, Save, Eye, BarChart3, Shuffle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';
import {
    ORIGENS, LOJAS, PROVEDORES, VIEWPORTS, CONFIG_PADRAO, normalizaConfig, taxa, somaMetricas,
} from '../../utils/redirectorAdmin';

const estiloCampo = { background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' };

/**
 * Redirector / Monetização: página intermediária de ir.supervitrineofertas.com.
 * Tudo estruturado (sem HTML/JS). O servidor normaliza de novo; a página nasce OFF.
 */
export default function RedirectorMonetizacao() {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const [cfg, setCfg] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const [prev, setPrev] = useState({ deal_id: '', provider: 'house', viewport: '360x640', url: '' });
    const [metricas, setMetricas] = useState(null);
    const [monet, setMonet] = useState(null);

    useEffect(() => {
        fetch(`${API}/admin/redirect-interstitial`, { headers })
            .then((r) => r.json()).then((d) => setCfg(normalizaConfig(d)))
            .catch(() => setCfg({ ...CONFIG_PADRAO }));
        fetch(`${API}/admin/redirect-interstitial/metrics?days=7`, { headers })
            .then((r) => r.json()).then((d) => { setMetricas(Array.isArray(d.rows) ? d.rows : []); setMonet(d.monetization || null); })
            .catch(() => setMetricas([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!cfg) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>;

    const set = (k, v) => setCfg((c) => normalizaConfig({ ...c, [k]: v }));
    const setMapa = (campo, k, v) => setCfg((c) => normalizaConfig({ ...c, [campo]: { ...(c[campo] || {}), [k]: v } }));
    const google = cfg.provider === 'google_adsense';

    const salvar = async () => {
        if (cfg.enabled && !ORIGENS.some((o) => cfg.sources[o.key])) {
            toast.error('Ative ao menos uma origem ou deixe a página desligada');
            return;
        }
        if (cfg.enabled && !window.confirm('Ligar a página intermediária afeta tráfego real das origens marcadas. Confirmar?')) return;
        setSalvando(true);
        try {
            const r = await fetch(`${API}/admin/redirect-interstitial`, { method: 'PUT', headers, body: JSON.stringify(cfg) });
            if (r.ok) { setCfg(normalizaConfig(await r.json())); toast.success('Configuração salva (vale em até 30 s)'); } else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        setSalvando(false);
    };

    const visualizar = async () => {
        if (!/^[A-Za-z0-9_-]{3,64}$/.test(prev.deal_id)) { toast.error('Informe um deal_id válido'); return; }
        const q = new URLSearchParams({ deal_id: prev.deal_id, provider: prev.provider });
        const r = await fetch(`${API}/admin/redirect-interstitial/preview-url?${q}`, { headers });
        if (!r.ok) { toast.error('Não foi possível gerar a pré-visualização'); return; }
        const { url } = await r.json();
        setPrev((p) => ({ ...p, url }));
    };

    const vp = VIEWPORTS.find((v) => v.key === prev.viewport) || VIEWPORTS[1];
    const tot = somaMetricas(metricas);

    return (
        <div className="space-y-4" data-testid="redirector-monetizacao">
            <div className="sv-card-system">
                <div className="flex items-center gap-2 mb-3"><Shuffle size={16} /><b style={{ color: 'var(--text)' }}>Página intermediária</b></div>
                <label className="flex items-center gap-2 text-xs mb-3" style={{ color: 'var(--text)' }}>
                    <input type="checkbox" checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} data-testid="ri-enabled" />
                    Ativa (desligada = fluxo atual do /r/, sem mudança)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <fieldset>
                        <legend className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Origens</legend>
                        {ORIGENS.map((o) => (
                            <label key={o.key} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text)' }}>
                                <input type="checkbox" checked={!!cfg.sources[o.key]} onChange={(e) => setMapa('sources', o.key, e.target.checked)} data-testid={`ri-src-${o.key}`} /> {o.label}
                            </label>
                        ))}
                    </fieldset>
                    <fieldset>
                        <legend className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Lojas (desmarcada = fluxo atual para a loja)</legend>
                        {LOJAS.map((l) => (
                            <label key={l.key} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text)' }}>
                                <input type="checkbox" checked={cfg.marketplaces[l.key] !== false} onChange={(e) => setMapa('marketplaces', l.key, e.target.checked)} data-testid={`ri-mkt-${l.key}`} /> {l.label}
                            </label>
                        ))}
                    </fieldset>
                    <label className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Provedor de anúncio
                        <select value={cfg.provider} onChange={(e) => set('provider', e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={estiloCampo} data-testid="ri-provider">
                            {PROVEDORES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                        </select>
                    </label>
                    <label className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Texto do botão principal
                        <input value={cfg.cta_text} maxLength={40} onChange={(e) => set('cta_text', e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={estiloCampo} data-testid="ri-cta" />
                    </label>
                    <label className="flex items-center gap-2 text-xs" style={{ color: google ? 'var(--muted-2)' : 'var(--text)' }}>
                        <input type="checkbox" disabled={google} checked={cfg.auto_redirect} onChange={(e) => set('auto_redirect', e.target.checked)} data-testid="ri-auto" />
                        Redirecionar sozinho {google && '(desligado com Google AdSense)'}
                    </label>
                    <label className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Segundos
                        <select value={cfg.auto_redirect_seconds} disabled={!cfg.auto_redirect} onChange={(e) => set('auto_redirect_seconds', e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={estiloCampo} data-testid="ri-secs">
                            {[3, 5, 10, 15].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </label>
                    {google && (
                        <>
                            <label className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Slot AdSense (só números; client vem do servidor)
                                <input value={cfg.google_slot} onChange={(e) => setCfg((c) => ({ ...c, google_slot: e.target.value.replace(/\D/g, '').slice(0, 12) }))} className="w-full px-3 py-2 rounded-lg text-xs" style={estiloCampo} data-testid="ri-gslot" />
                            </label>
                            <label className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Dispositivo
                                <select value={cfg.google_device} onChange={(e) => set('google_device', e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={estiloCampo}>
                                    <option value="all">Todos</option><option value="mobile">Mobile</option><option value="desktop">Desktop</option>
                                </select>
                            </label>
                        </>
                    )}
                </div>
                <button className="sv-btn-gold-system sv-btn-system mt-3" onClick={salvar} disabled={salvando} data-testid="ri-save">
                    <Save size={14} /><span className="ml-1">Salvar configuração</span>
                </button>
            </div>

            <div className="sv-card-system">
                <div className="flex items-center gap-2 mb-3"><Eye size={16} /><b style={{ color: 'var(--text)' }}>Visualizar página</b></div>
                <p className="text-[11px] mb-2" style={{ color: 'var(--muted-2)' }}>Pré-visualização assinada (30 min): não grava visita, impressão nem clique, e o botão fica inerte.</p>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
                    <input placeholder="deal_id" value={prev.deal_id} onChange={(e) => setPrev((p) => ({ ...p, deal_id: e.target.value.trim(), url: '' }))} className="px-3 py-2 rounded-lg text-xs" style={estiloCampo} data-testid="ri-prev-deal" />
                    <select value={prev.provider} onChange={(e) => setPrev((p) => ({ ...p, provider: e.target.value, url: '' }))} className="px-3 py-2 rounded-lg text-xs" style={estiloCampo} data-testid="ri-prev-provider">
                        {PROVEDORES.map((p) => <option key={p.key} value={p.key}>{p.key === 'google_adsense' ? 'Google (placeholder)' : p.label}</option>)}
                    </select>
                    <select value={prev.viewport} onChange={(e) => setPrev((p) => ({ ...p, viewport: e.target.value }))} className="px-3 py-2 rounded-lg text-xs" style={estiloCampo} data-testid="ri-prev-viewport">
                        {VIEWPORTS.map((v) => <option key={v.key} value={v.key}>{v.key}</option>)}
                    </select>
                    <button className="sv-btn-system" onClick={visualizar} data-testid="ri-prev-btn">Visualizar página</button>
                </div>
                {prev.url && (
                    <div className="overflow-auto" style={{ maxWidth: '100%' }}>
                        <iframe title="Pré-visualização da página intermediária" src={prev.url} width={vp.w} height={vp.h}
                            sandbox="allow-same-origin" style={{ border: '1px solid var(--border)', borderRadius: 12, background: '#fff' }} data-testid="ri-prev-frame" />
                    </div>
                )}
            </div>

            <div className="sv-card-system">
                <div className="flex items-center gap-2 mb-3"><BarChart3 size={16} /><b style={{ color: 'var(--text)' }}>Métricas (7 dias)</b></div>
                {metricas === null ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3" style={{ color: 'var(--text)' }} data-testid="ri-metric-totals">
                            <div>Visitas: <b>{tot.page_view || 0}</b></div>
                            <div>Ir para a oferta: <b>{tot.offer_continue_click || 0}</b> ({taxa(tot.offer_continue_click || 0, tot.page_view || 0)}, N={tot.page_view || 0})</div>
                            <div>Redirecionamentos: <b>{tot.affiliate_redirect || 0}</b></div>
                            <div>Anúncios: <b>{tot.ad_impression || 0}</b> impr. / <b>{tot.ad_click || 0}</b> cliques ({taxa(tot.ad_click || 0, tot.ad_impression || 0)}, N={tot.ad_impression || 0})</div>
                        </div>
                        {monet && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mb-3" style={{ color: 'var(--text)' }} data-testid="ri-monetizacao">
                                {[['monetized', 'Monetizado (link oficial ML)'], ['non_monetized', 'Sem comissão (ML sem link oficial)']].map(([k, rot]) => (
                                    <div key={k} className="rounded-lg px-3 py-2" style={{ border: '1px solid var(--border)' }} data-testid={`ri-${k}`}>
                                        <b>{rot}</b>
                                        <div>Visitas: <b>{monet[k]?.page_views || 0}</b> · Continuar: <b>{monet[k]?.continue_clicks || 0}</b> · Redirecionamentos: <b>{monet[k]?.affiliate_redirects || 0}</b></div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {metricas.length > 0 && (
                            <table className="w-full text-[11px]" style={{ color: 'var(--text)' }} data-testid="ri-metric-table">
                                <thead><tr><th>Dia</th><th>Origem</th><th>Loja</th><th>Campanha</th><th>Visitas</th><th>Continuar</th><th>Taxa</th><th>Impr.</th><th>Cliques</th><th>CTR</th></tr></thead>
                                <tbody>{metricas.map((m) => (
                                    <tr key={`${m.day}-${m.source}-${m.marketplace}-${m.campaign_id}`}>
                                        <td>{m.day}</td><td>{m.source}</td><td>{m.marketplace}</td><td>{m.campaign_id}</td>
                                        <td>{m.page_view}</td><td>{m.offer_continue_click}</td><td>{taxa(m.offer_continue_click, m.page_view)}</td>
                                        <td>{m.ad_impression}</td><td>{m.ad_click}</td><td>{taxa(m.ad_click, m.ad_impression)}</td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
